import { BOT_API_BASE } from '@/config/routes';
import { getTabId } from '@/shared/lib/tab-id';
import { mockGetBotEnvironments } from '../mocks/mock-api';
import { getBotId, getChannel, getUserId, isUsingMockApi, throwIfNotOk } from './api';

// This endpoint is DM's `/v4/bot/environments`, reached through aistudio's
// generic `/api/resources/bot` proxy — NOT the observation-run service, so it
// must NOT carry observation-run's `x-version: 2` header (see
// `buildIdentityHeaders` in `api.ts`). `x-version: 4` is this endpoint's own
// contract (mirrors agent-tools' `environmentsApi` client).
//
// Every other header here mirrors what aistudio's OWN axios interceptor
// (`client/services/http.js`) attaches to every request unconditionally:
// `x-bot-id`, `x-user-id`, `x-channel`/`x-service-desk` (both read the same
// `bots.currentSupportDesk` field), and `x-tab-id`. Missing `x-user-id`
// specifically 500s server-side rather than 401ing.
function buildEnvironmentsHeaders(): Headers {
  const headers = new Headers();
  headers.set('accept', 'application/json');
  headers.set('x-version', '4');
  headers.set('x-tab-id', getTabId());
  const botId = getBotId();
  if (botId) headers.set('x-bot-id', botId);
  const userId = getUserId();
  if (userId) headers.set('x-user-id', userId);
  const channel = getChannel();
  if (channel) {
    headers.set('x-channel', channel);
    headers.set('x-service-desk', channel);
  }
  return headers;
}

/**
 * One rung of the environment-promotion ladder. Each environment is its own
 * environment-scoped bot (`botRefId`) under the parent bot. `envOrder` is the
 * ladder position — the lowest is the authoring (draft) env. `releaseEnv` is
 * the stable slug Calibration uses as the environment's form value; `label` is
 * the display name. `manifestId`/`manifestBuild` describe the env's currently
 * *active* build — the only build a run can evaluate.
 *
 * Same payload agent-tools and integration-builder consume (heimdall's
 * `BotEnvironment`), and the same fields aistudio reads off
 * `store.state.scaffold.environments`.
 */
export interface BotEnvironment {
  botId?: string;
  botRefId: string;
  label: string;
  releaseEnv: string;
  envOrder: number;
  isDeletable?: boolean;
  manifestId?: string;
  manifestBuild?: string;
}

/**
 * Fetches the bot's environment ladder. Mirrors `GET /v4/bot/environments`
 * (heimdall's `fetchBotEnvironmentsApi`) via the `/api/resources/bot` proxy.
 * Only meaningful for EP (`DYNAMIC_ENV`) bots — non-EP bots have no ladder.
 *
 * `manifestDetails=true` is required — aistudio's own bootstrap call
 * (`Init/index.vue`, which populates `store.state.scaffold.environments`)
 * passes it, and without it the response omits `manifestBuild`/`manifestId`
 * per env entirely, which silently filters every environment out downstream.
 */
async function realGetBotEnvironments(signal?: AbortSignal): Promise<BotEnvironment[]> {
  const res = await fetch(`${BOT_API_BASE}/environments?manifestDetails=true`, {
    headers: buildEnvironmentsHeaders(),
    credentials: 'include',
    signal,
  });
  await throwIfNotOk(res);
  const body = (await res.json()) as BotEnvironment[] | { payload?: BotEnvironment[] };
  // The endpoint may return the array directly or wrapped in `{ payload: [...] }`.
  if (Array.isArray(body)) return body;
  return Array.isArray(body?.payload) ? body.payload : [];
}

// Dispatched like lib/api.ts's own functions, same isUsingMockApi() flag —
// only reached for EP bots (see use-calibration-environments.ts), so mock
// mode only matters here for a bot whose seeded currentBot.envProductType is
// DYNAMIC_ENV; the standalone-dev default (non-EP) never calls this at all.
export const getBotEnvironments: typeof realGetBotEnvironments = (...args) =>
  (isUsingMockApi() ? mockGetBotEnvironments : realGetBotEnvironments)(...args);
